import React from "react";

// Global crash guard: no more dead black screens. Shows a friendly reload
// card and reports the crash to the backend so we can diagnose remotely.
function report(kind, message, stack) {
  try {
    const base = process.env.REACT_APP_BACKEND_URL;
    const payload = JSON.stringify({
      kind,
      message: String(message || "").slice(0, 500),
      stack: String(stack || "").slice(0, 3000),
      url: window.location.href.slice(0, 300),
      user_agent: navigator.userAgent.slice(0, 300),
      at: new Date().toISOString(),
    });
    if (navigator.sendBeacon) {
      navigator.sendBeacon(`${base}/api/client-errors`, new Blob([payload], { type: "application/json" }));
    } else {
      fetch(`${base}/api/client-errors`, { method: "POST", headers: { "Content-Type": "application/json" }, body: payload, keepalive: true });
    }
  } catch { /* never crash the crash reporter */ }
}

const IGNORE = /ResizeObserver loop|Script error\.?$|Java object is gone|Error invoking postMessage|__gCrWeb|window\.webkit\.messageHandlers|The operation was aborted|Load failed$/i;

// Errors thrown by scripts injected into in-app browsers (Facebook/Instagram
// "iabjs://" loggers) or browser extensions — not Fieldlink code.
const THIRD_PARTY_SRC = /^(iabjs|chrome-extension|moz-extension|safari(-web)?-extension|webkit-masked-url):/i;

if (typeof window !== "undefined" && !window.__flErrHooked) {
  window.__flErrHooked = true;
  window.addEventListener("error", (e) => {
    if (IGNORE.test(e.message || "")) return;
    if (THIRD_PARTY_SRC.test(e.filename || "")) return;
    if (THIRD_PARTY_SRC.test((e.error?.stack || "").match(/at .*?\(?([a-z-]+:\/\/[^\s)]+)/i)?.[1] || "")) return;
    report("window.error", e.message, e.error?.stack);
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e.reason;
    // Ignore normal API errors (axios) — only report real crashes
    if (r && r.isAxiosError) return;
    if (IGNORE.test(r?.message || "")) return;
    report("unhandledrejection", r?.message || String(r), r?.stack);
  });
}

export class InlineErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    report("react.inline", error?.message, `${error?.stack || ""}\n${info?.componentStack || ""}`);
  }

  componentDidUpdate(prev) {
    // Reset the boundary when the caller changes resetKey (e.g. a different
    // plan/page is opened, or the user taps "Try again").
    if (prev.resetKey !== this.props.resetKey && this.state.crashed) {
      this.setState({ crashed: false });
    }
  }

  render() {
    if (this.state.crashed) return this.props.fallback ?? null;
    return this.props.children;
  }
}

export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { crashed: false };
  }

  static getDerivedStateFromError() {
    return { crashed: true };
  }

  componentDidCatch(error, info) {
    report("react.crash", error?.message, `${error?.stack || ""}\n${info?.componentStack || ""}`);
    // Self-heal: if the crash happened on a stale bundle (a deploy landed
    // since this PWA session loaded), reload once to pick up the fixed code.
    try {
      if (sessionStorage.getItem("fl_crash_reloaded")) return;
      const current = Array.from(document.scripts)
        .map((s) => s.src)
        .find((s) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(s));
      if (!current) return;
      fetch("/", { cache: "no-store" })
        .then((r) => r.text())
        .then((html) => {
          const m = html.match(/\/static\/js\/(main\.[a-z0-9]+\.js)/);
          if (m && !current.includes(m[1])) {
            sessionStorage.setItem("fl_crash_reloaded", "1");
            window.location.reload();
          }
        })
        .catch(() => {});
    } catch { /* never crash the crash handler */ }
  }

  render() {
    if (!this.state.crashed) return this.props.children;
    return (
      <div style={{
        minHeight: "100vh", background: "#09090B", color: "#fff",
        display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
      }} data-testid="crash-screen">
        <div style={{ maxWidth: 420, textAlign: "center" }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>⚠️</div>
          <h1 style={{ fontSize: 22, fontWeight: 800, textTransform: "uppercase", letterSpacing: 1 }}>
            Something went wrong
          </h1>
          <p style={{ color: "#a1a1aa", fontSize: 14, marginTop: 8 }}>
            Sorry — the app hit a snag. Your work is safe. Tap reload to carry on;
            we've been notified automatically.
          </p>
          <button
            onClick={() => window.location.reload()}
            data-testid="crash-reload-btn"
            style={{
              marginTop: 20, background: "#FF5F15", color: "#000", border: "none",
              padding: "12px 28px", fontWeight: 800, textTransform: "uppercase",
              letterSpacing: 1, fontSize: 13, cursor: "pointer",
            }}
          >
            Reload Fieldlink
          </button>
        </div>
      </div>
    );
  }
}
